/**
 * A floor plan drawn by hand, where there was no scan.
 *
 * WHY. Not every survey has a LiDAR capture: an older phone, a refused scan, a
 * property visited before the app existed, a plan taken off a drawing. Both
 * competing tools let the surveyor draw the plan instead — a room at a time,
 * on a grid, with the wall lengths typed in millimetres. Without that, a
 * survey with no scan has no plan, and so no heat loss at all.
 *
 * WHAT IS STORED, AND WHY IT IS NOT A CAPTURE. What the surveyor draws is a
 * handful of rectangles: where each room sits, how big it is, which way round
 * it is, how high its ceiling is. That is what this file models, and that is
 * what the database stores (`survey_drawings`, migration 0047) — because those
 * are the numbers the surveyor TYPED. Store the capture instead and "4200 mm"
 * becomes a 4×4 matrix that has to be solved back into a rectangle before it
 * can be edited, and a millimetre of floating-point drift turns a typed figure
 * into 4199 mm.
 *
 * WHAT EVERYTHING ELSE SEES IS A CAPTURE. `drawnPlanCapture` turns the
 * rectangles into exactly the shape a RoomPlan scan arrives in — rooms of
 * walls and floors with transforms and dimensions. So the plan on screen, the
 * plan's corrections (0039), the radiators and the heat pump (0032, 0044), the
 * heat-loss conversion, the design check, the exports, the installer pack and
 * the customer's share viewer all read a hand-drawn survey through the code
 * they already have. Nothing downstream knows the difference, which is the
 * whole point: a second geometry format would be a second thing to keep
 * correct, and the two would disagree the first time either changed.
 *
 * The one thing that must NOT be lost in that translation is where the figures
 * came from. A drawn plan is a surveyor's statement, not a measurement, and
 * every drawing and document that says where its dimensions come from says
 * "Drawn by hand, not scanned" for one of these. See `plan-export.ts`.
 *
 * IDENTIFIERS ARE STABLE. A room's id and its walls' ids are derived from the
 * drawn room's own id and never change when it is moved, resized or renamed.
 * That is what lets a redraw be MERGED into the survey's rooms field by field
 * (`scan-merge.ts`) rather than replacing them: a U-value somebody confirmed
 * survives the surveyor nudging a wall, and a radiator placed on a wall stays
 * on that wall.
 *
 * ROOMS ARE RECTANGLES. Deliberately: it is what a person can draw accurately
 * with a mouse and a keyboard, it is what both competitors offer, and it is
 * exactly representable as a captured surface (centre, size, rotation) with no
 * polygon to go wrong. An L-shaped room is drawn as two rooms sharing a wall —
 * which `convertCapture`'s wall pairing then reads as an internal partition,
 * as it would two scanned rooms.
 *
 * THE FRAME is the plan frame `scan-plan.ts` documents: x is world X, y is
 * world Z, both metres, y growing down the screen. A room's own X axis is
 * `rotationDeg` clockwise from plan +x.
 */

import type { CapturedRoom, CapturedSection, CapturedStructure, CapturedSurface } from "./roomplan.js";
import type { PlanPoint } from "./scan-plan.js";
import { storyLabel } from "./storey-name.js";

/** Bumped only if the stored shape changes in a way a reader must know about. */
export const DRAWN_PLAN_VERSION = 1;

/** One rectangular room, as drawn. All lengths in metres. */
export interface DrawnRoom {
  /** Stable for the life of the room: every generated identifier hangs off it. */
  readonly id: string;
  /**
   * What the surveyor calls it — "Kitchen", "Bedroom", "Front bedroom".
   * Passed to the conversion as the room's section label, so a name the
   * conversion recognises also settles the room's type and design
   * temperature, and one it does not is flagged for the designer to confirm.
   * Nothing here guesses a room type.
   */
  readonly name: string;
  readonly story: number;
  /** Centre of the rectangle, plan metres. */
  readonly centre: PlanPoint;
  /** Along the room's own X axis. */
  readonly widthM: number;
  /** Along the room's own Y axis. */
  readonly depthM: number;
  /** Clockwise from plan +x. */
  readonly rotationDeg: number;
  /**
   * Floor to ceiling. TYPED BY THE SURVEYOR, never defaulted here: it is the
   * room's volume, and so its ventilation heat loss. A drawing with a room
   * missing it is refused by `validateDrawnPlan` rather than filled in.
   */
  readonly ceilingHeightM: number;
}

/** A storey of the drawing. Present even before it has rooms. */
export interface DrawnStorey {
  readonly story: number;
  /** A name somebody typed. Blank or absent means the UK rule (`storyLabel`). */
  readonly name?: string;
}

export interface DrawnPlan {
  readonly version: number;
  readonly storeys: readonly DrawnStorey[];
  readonly rooms: readonly DrawnRoom[];
}

export const EMPTY_DRAWN_PLAN: DrawnPlan = {
  version: DRAWN_PLAN_VERSION,
  storeys: [{ story: 0 }],
  rooms: [],
};

/** The grid a dragged-out room lands on: 100 mm, the unit a plan is drawn in. */
export const DRAWN_GRID_M = 0.1;

/**
 * How near another room's wall a drawn room has to be before it is pulled onto
 * it. 300 mm is about the width of the walls it is standing in for, and well
 * inside the accuracy anybody claims for a hand drawing.
 */
export const DRAWN_SNAP_M = 0.3;

/** Rooms this size or smaller are not rooms; the smallest UK WC is about 0.8 m. */
export const DRAWN_MIN_SIDE_M = 0.3;

/** A ceiling this far from ordinary is a typing slip, not a room. */
export const DRAWN_CEILING_RANGE_M = { min: 1.5, max: 6 } as const;

// ---------------------------------------------------------------------------
// The rectangle
// ---------------------------------------------------------------------------

const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

/** The room's own axes in the plan frame: X along its width, Y along its depth. */
export function drawnRoomAxes(room: Pick<DrawnRoom, "rotationDeg">): {
  readonly x: PlanPoint;
  readonly y: PlanPoint;
} {
  const t = (room.rotationDeg * Math.PI) / 180;
  const c = round6(Math.cos(t));
  const s = round6(Math.sin(t));
  // Plan y grows downward, so +90 degrees clockwise on screen is (-s, c).
  return { x: { x: c, y: s }, y: { x: -s, y: c } };
}

/**
 * The room's four corners, in order, starting at the near-left one and going
 * round the way the walls are numbered. Wall `i` runs from corner `i` to
 * corner `i + 1`, so wall 0 spans the width, wall 1 the depth, and so on.
 */
export function drawnRoomCorners(room: DrawnRoom): readonly PlanPoint[] {
  const { x: ax, y: ay } = drawnRoomAxes(room);
  const hw = room.widthM / 2;
  const hd = room.depthM / 2;
  const at = (sw: number, sd: number): PlanPoint => ({
    x: round6(room.centre.x + ax.x * hw * sw + ay.x * hd * sd),
    y: round6(room.centre.y + ax.y * hw * sw + ay.y * hd * sd),
  });
  return [at(-1, -1), at(1, -1), at(1, 1), at(-1, 1)];
}

export interface DrawnWall {
  readonly index: number;
  readonly start: PlanPoint;
  readonly end: PlanPoint;
  readonly lengthM: number;
}

/** The room's four walls, in the order `drawnRoomCorners` sets. */
export function drawnRoomWalls(room: DrawnRoom): readonly DrawnWall[] {
  const corners = drawnRoomCorners(room);
  return corners.map((start, index) => {
    const end = corners[(index + 1) % corners.length] as PlanPoint;
    return {
      index,
      start,
      end,
      lengthM: index % 2 === 0 ? room.widthM : room.depthM,
    };
  });
}

/** Plan-metre area of the room, for the room list and the storey totals. */
export function drawnRoomAreaM2(room: DrawnRoom): number {
  return round6(room.widthM * room.depthM);
}

/**
 * The room with one wall set to a typed length — the competitors' "Length …
 * Apply", and the only way a hand-drawn dimension is ever exact.
 *
 * THE WALL GROWS FROM ITS STARTING CORNER. A room is nearly always resized
 * after it has been placed against its neighbours, so the rule has to say
 * which end of it moves. The wall keeps the corner it starts at — the corner
 * it shares with the wall before it — and grows away from that, so those two
 * walls stay exactly where they are and a room already snapped to the room on
 * that side stays snapped. Only the far side of the room moves.
 *
 * Returns the room unchanged for a length that is not a real one — a room
 * silently becoming 0 mm wide would be a worse answer than a refused edit.
 */
export function setDrawnWallLength(room: DrawnRoom, wallIndex: number, lengthM: number): DrawnRoom {
  if (!Number.isFinite(lengthM) || lengthM < DRAWN_MIN_SIDE_M) return room;
  const index = ((wallIndex % 4) + 4) % 4;
  const axes = drawnRoomAxes(room);
  // Walls 0 and 2 span the room's width, walls 1 and 3 its depth.
  const spansWidth = index % 2 === 0;
  const was = spansWidth ? room.widthM : room.depthM;
  const delta = lengthM - was;
  if (delta === 0) return room;

  // Walls are numbered round the room, so walls 0 and 1 run along their axis
  // and walls 2 and 3 run back against it. Growing from the starting corner
  // therefore grows the room the way the wall runs.
  const towards = index === 0 || index === 1 ? 1 : -1;
  const axis = spansWidth ? axes.x : axes.y;
  const half = (delta / 2) * towards;
  return {
    ...room,
    ...(spansWidth ? { widthM: round6(lengthM) } : { depthM: round6(lengthM) }),
    centre: { x: round6(room.centre.x + axis.x * half), y: round6(room.centre.y + axis.y * half) },
  };
}

/** The room turned a quarter turn clockwise about its own centre, `turns` times. */
export function rotateDrawnRoom(room: DrawnRoom, turns = 1): DrawnRoom {
  const degrees = (((room.rotationDeg + turns * 90) % 360) + 360) % 360;
  return { ...room, rotationDeg: degrees };
}

/** The room moved by a plan-metre offset. */
export function moveDrawnRoom(room: DrawnRoom, dx: number, dy: number): DrawnRoom {
  return { ...room, centre: { x: round6(room.centre.x + dx), y: round6(room.centre.y + dy) } };
}

/** A plan metre rounded to the drawing grid. */
export function snapToGrid(value: number, gridM = DRAWN_GRID_M): number {
  return round6(Math.round(value / gridM) * gridM);
}

// ---------------------------------------------------------------------------
// Snapping
// ---------------------------------------------------------------------------

export type DrawnSnapKind = "corner" | "wall" | "grid";

export interface DrawnSnap {
  readonly room: DrawnRoom;
  readonly kind: DrawnSnapKind;
  /** How far the room was moved, metres. 0 when it was already there. */
  readonly movedM: number;
}

const distance = (a: PlanPoint, b: PlanPoint): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * A room pulled onto its neighbours, so a terrace of rooms shares walls
 * instead of overlapping or leaving a sliver of nothing between them.
 *
 * THE WHOLE ROOM MOVES. Never one corner, never one wall: the room is
 * TRANSLATED so that a corner (or a wall) of it lands exactly on a
 * neighbour's. That is the rule the rest of the feature rests on — a room the
 * surveyor typed as 4200 × 3100 is still 4200 × 3100 after it is snapped.
 * Dragging the corner instead would silently change a dimension somebody had
 * measured, which is the one thing a drawing tool must not do.
 *
 * Two tiers, corners first, for the same reason `snapPlanPoint` prefers them:
 * corner to corner is what a person means, and a wall snap that slides the
 * room a few hundred millimetres along the wall instead would put it in the
 * wrong place while looking right.
 *
 * Only rooms on the same storey are considered, and only the offset within
 * `toleranceM` is applied: a room dragged out on its own falls back to the
 * grid, so it is still square with everything else.
 */
export function snapDrawnRoom(
  room: DrawnRoom,
  others: readonly DrawnRoom[],
  toleranceM = DRAWN_SNAP_M,
): DrawnSnap {
  const neighbours = others.filter((o) => o.id !== room.id && o.story === room.story);
  const mine = drawnRoomCorners(room);

  // Tier 1: a corner of this room onto a corner of another.
  let best: { dx: number; dy: number; d: number } | undefined;
  for (const other of neighbours) {
    for (const theirs of drawnRoomCorners(other)) {
      for (const ours of mine) {
        const d = distance(ours, theirs);
        if (d <= toleranceM && (best === undefined || d < best.d)) {
          best = { dx: theirs.x - ours.x, dy: theirs.y - ours.y, d };
        }
      }
    }
  }
  if (best) {
    return { room: moveDrawnRoom(room, best.dx, best.dy), kind: "corner", movedM: round6(best.d) };
  }

  // Tier 2: a wall of this room onto a parallel wall of another, moving only
  // across the wall — along it, the surveyor's placement is kept.
  const myWalls = drawnRoomWalls(room);
  let bestWall: { dx: number; dy: number; d: number } | undefined;
  for (const other of neighbours) {
    for (const theirs of drawnRoomWalls(other)) {
      const tu = unitOf(theirs);
      if (!tu) continue;
      for (const ours of myWalls) {
        const ou = unitOf(ours);
        if (!ou) continue;
        // Parallel to within about 2 degrees; anything else is a different wall.
        if (Math.abs(ou.x * tu.x + ou.y * tu.y) < 0.999) continue;
        // Perpendicular offset from our wall's midpoint to their wall's line.
        const n = { x: -tu.y, y: tu.x };
        const mid = { x: (ours.start.x + ours.end.x) / 2, y: (ours.start.y + ours.end.y) / 2 };
        const gap = (mid.x - theirs.start.x) * n.x + (mid.y - theirs.start.y) * n.y;
        const d = Math.abs(gap);
        if (d > toleranceM || d === 0) continue;
        // The walls must actually face each other: their runs have to overlap.
        if (!overlaps(ours, theirs, tu)) continue;
        if (bestWall === undefined || d < bestWall.d) {
          bestWall = { dx: -n.x * gap, dy: -n.y * gap, d };
        }
      }
    }
  }
  if (bestWall) {
    return {
      room: moveDrawnRoom(room, bestWall.dx, bestWall.dy),
      kind: "wall",
      movedM: round6(bestWall.d),
    };
  }

  const snapped: DrawnRoom = {
    ...room,
    centre: { x: snapToGrid(room.centre.x), y: snapToGrid(room.centre.y) },
  };
  return { room: snapped, kind: "grid", movedM: round6(distance(room.centre, snapped.centre)) };
}

function unitOf(wall: DrawnWall): PlanPoint | undefined {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const l = Math.hypot(dx, dy);
  if (!(l > 1e-9)) return undefined;
  return { x: dx / l, y: dy / l };
}

/** Do two parallel walls face each other along their shared direction? */
function overlaps(ours: DrawnWall, theirs: DrawnWall, u: PlanPoint): boolean {
  const at = (p: PlanPoint): number => p.x * u.x + p.y * u.y;
  const a0 = Math.min(at(ours.start), at(ours.end));
  const a1 = Math.max(at(ours.start), at(ours.end));
  const b0 = Math.min(at(theirs.start), at(theirs.end));
  const b1 = Math.max(at(theirs.start), at(theirs.end));
  return Math.min(a1, b1) - Math.max(a0, b0) > 1e-6;
}

// ---------------------------------------------------------------------------
// Splitting
// ---------------------------------------------------------------------------

/**
 * One room cut in two, across its width or along it — a through lounge made
 * into a lounge and a dining room without redrawing either.
 *
 * The cut is at `atM` measured from the room's own near edge along the axis
 * being cut, so the number the surveyor types is the size of the first of the
 * two rooms. The two come out sharing the wall the cut made, each keeping the
 * ceiling height; the first keeps the room's name and the second is given one
 * by the caller. Both keep the rotation, so a room drawn at an angle splits
 * square to itself rather than to the page.
 *
 * `undefined` when the cut would leave either side smaller than a room.
 */
export function splitDrawnRoom(
  room: DrawnRoom,
  axis: "width" | "depth",
  atM: number,
  secondId: string,
  secondName: string,
): readonly [DrawnRoom, DrawnRoom] | undefined {
  const span = axis === "width" ? room.widthM : room.depthM;
  if (!Number.isFinite(atM)) return undefined;
  if (atM < DRAWN_MIN_SIDE_M || span - atM < DRAWN_MIN_SIDE_M) return undefined;

  const axes = drawnRoomAxes(room);
  const along = axis === "width" ? axes.x : axes.y;
  // Centres: the first room's centre sits atM/2 from the near edge, the
  // second's (span + atM)/2 — both measured from the old centre.
  const firstOffset = -span / 2 + atM / 2;
  const secondOffset = atM + (span - atM) / 2 - span / 2;
  const sized = (size: number): Partial<DrawnRoom> =>
    axis === "width" ? { widthM: round6(size) } : { depthM: round6(size) };

  const first: DrawnRoom = {
    ...room,
    ...sized(atM),
    centre: {
      x: round6(room.centre.x + along.x * firstOffset),
      y: round6(room.centre.y + along.y * firstOffset),
    },
  };
  const second: DrawnRoom = {
    ...room,
    ...sized(span - atM),
    id: secondId,
    name: secondName,
    centre: {
      x: round6(room.centre.x + along.x * secondOffset),
      y: round6(room.centre.y + along.y * secondOffset),
    },
  };
  return [first, second];
}

// ---------------------------------------------------------------------------
// Storeys
// ---------------------------------------------------------------------------

/** The storeys of the drawing, lowest first, each with the name it is shown by. */
export function drawnStoreys(plan: DrawnPlan): readonly { readonly story: number; readonly label: string }[] {
  const levels = new Map<number, string | undefined>();
  for (const storey of plan.storeys) levels.set(storey.story, storey.name);
  for (const room of plan.rooms) if (!levels.has(room.story)) levels.set(room.story, undefined);
  return [...levels.entries()]
    .sort(([a], [b]) => a - b)
    .map(([story, name]) => ({ story, label: storyLabel(story, name) }));
}

/** The next storey up, or down, that the drawing does not have yet. */
export function nextDrawnStorey(plan: DrawnPlan, direction: "up" | "down"): number {
  const levels = drawnStoreys(plan).map((s) => s.story);
  if (levels.length === 0) return 0;
  return direction === "up"
    ? (Math.max(...levels) as number) + 1
    : (Math.min(...levels) as number) - 1;
}

export function addDrawnStorey(plan: DrawnPlan, story: number, name?: string): DrawnPlan {
  if (plan.storeys.some((s) => s.story === story)) return plan;
  return {
    ...plan,
    storeys: [...plan.storeys, { story, ...(name ? { name } : {}) }].sort((a, b) => a.story - b.story),
  };
}

/**
 * A storey removed — only when it is empty. A storey with rooms on it is not
 * deleted quietly: the caller deletes the rooms first, deliberately, or keeps
 * the storey.
 */
export function removeDrawnStorey(plan: DrawnPlan, story: number): DrawnPlan {
  if (plan.rooms.some((r) => r.story === story)) return plan;
  return { ...plan, storeys: plan.storeys.filter((s) => s.story !== story) };
}

/**
 * Every room of one storey copied onto another — the competitors' "duplicate
 * floor", and the reason a three-bedroom semi takes one drawing session rather
 * than two.
 *
 * The copies are new rooms with new ids (`idFor` is handed the source room so
 * a caller can mint "dr-7" or a uuid as it likes), in the same places, with
 * the same names, sizes and ceiling heights. Rooms already on the destination
 * storey are kept: a copy adds, it does not replace. Copying onto a storey
 * that is not in the drawing yet adds it.
 */
export function copyDrawnStorey(
  plan: DrawnPlan,
  from: number,
  to: number,
  idFor: (room: DrawnRoom, index: number) => string,
): DrawnPlan {
  if (from === to) return plan;
  const source = plan.rooms.filter((r) => r.story === from);
  if (source.length === 0) return plan;
  const copies = source.map((room, index) => ({ ...room, id: idFor(room, index), story: to }));
  return {
    ...addDrawnStorey(plan, to),
    rooms: [...plan.rooms, ...copies],
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * What is wrong with the drawing, in the words the API answers with. Empty
 * means it can be stored and converted.
 *
 * Everything here is a refusal, not a correction: a room with no ceiling
 * height is not given one, because that height is the room's volume and so
 * part of its heat loss.
 */
export function validateDrawnPlan(plan: DrawnPlan): readonly string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  if (!Array.isArray(plan.rooms)) return ["The drawing has no rooms array."];

  for (const room of plan.rooms) {
    const where = room.name?.trim() ? `"${room.name.trim()}"` : `room ${room.id}`;
    if (typeof room.id !== "string" || room.id.trim() === "") {
      issues.push("Every drawn room needs an id.");
      continue;
    }
    if (ids.has(room.id)) issues.push(`Two rooms share the id ${room.id}.`);
    ids.add(room.id);
    if (typeof room.name !== "string" || room.name.trim() === "") {
      issues.push(`${where} has no name. Name the room so the heat loss can say which room it is.`);
    }
    if (!Number.isInteger(room.story)) issues.push(`${where} is not on a whole storey.`);
    if (!(room.widthM >= DRAWN_MIN_SIDE_M) || !(room.depthM >= DRAWN_MIN_SIDE_M)) {
      issues.push(
        `${where} is smaller than ${DRAWN_MIN_SIDE_M * 1000} mm on one side, which is not a room.`,
      );
    }
    if (!Number.isFinite(room.centre?.x) || !Number.isFinite(room.centre?.y)) {
      issues.push(`${where} has no place on the plan.`);
    }
    if (!Number.isFinite(room.rotationDeg)) issues.push(`${where} has no rotation.`);
    if (
      !Number.isFinite(room.ceilingHeightM) ||
      room.ceilingHeightM < DRAWN_CEILING_RANGE_M.min ||
      room.ceilingHeightM > DRAWN_CEILING_RANGE_M.max
    ) {
      issues.push(
        `${where} needs a floor-to-ceiling height between ${DRAWN_CEILING_RANGE_M.min} m and ` +
          `${DRAWN_CEILING_RANGE_M.max} m. It is the room's volume, so nothing here guesses it.`,
      );
    }
  }
  return issues;
}

/**
 * A short string that changes whenever the drawing would produce different
 * geometry — the drawn twin of `planEditsSignature`, and what says whether a
 * heat loss already calculated is still the heat loss of this drawing. Names
 * are not in it: renaming a room does not move a wall.
 */
export function drawnPlanSignature(plan: DrawnPlan): string {
  const rooms = [...plan.rooms]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((r) =>
      [
        r.id,
        r.story,
        mm(r.centre.x),
        mm(r.centre.y),
        mm(r.widthM),
        mm(r.depthM),
        Math.round(r.rotationDeg * 100),
        mm(r.ceilingHeightM),
      ].join(","),
    );
  return rooms.join("|");
}

const mm = (metres: number): number => Math.round(metres * 1000);

// ---------------------------------------------------------------------------
// The capture
// ---------------------------------------------------------------------------

/** `${room.id}:floor`, `${room.id}:wall-0` … — stable, and stable is the point. */
export function drawnFloorId(roomId: string): string {
  return `${roomId}:floor`;
}

export function drawnWallId(roomId: string, wallIndex: number): string {
  return `${roomId}:wall-${wallIndex}`;
}

/**
 * Where each storey's floor sits in world Y.
 *
 * The plan drops height, so nothing on screen and nothing in the heat loss
 * depends on this. What does depend on it is the question "would a radiator
 * fit under this window?", which is asked of a wall's bottom and top edges —
 * so the storeys have to stack in the right order and not sit inside one
 * another. They are stacked floor to ceiling on the tallest room of each
 * storey, with no allowance for the thickness of the floor between them: a
 * hand drawing records no such thing, and inventing one would put every
 * upstairs sill at a height nobody measured.
 */
export function drawnStoreyFloorY(plan: DrawnPlan, story: number): number {
  const heights = new Map<number, number>();
  for (const room of plan.rooms) {
    const tallest = heights.get(room.story) ?? 0;
    if (room.ceilingHeightM > tallest) heights.set(room.story, room.ceilingHeightM);
  }
  let y = 0;
  if (story > 0) {
    for (let level = 0; level < story; level += 1) y += heights.get(level) ?? 0;
  } else if (story < 0) {
    for (let level = story; level < 0; level += 1) y -= heights.get(level) ?? 0;
  }
  return round6(y);
}

/**
 * The drawing as a capture: the shape everything downstream already reads.
 *
 * Each room becomes one `CapturedRoom` with four walls and one floor, built
 * the way `scan-plan.ts` documents the transforms — column 0 the surface's own
 * X axis in world space, column 1 its Y, column 3 its centre — so the plan
 * projection, the conversion and the 3D view read them exactly as they read a
 * scan's.
 *
 * The room's name is carried as its section label, which is where
 * `convertCapture` looks for a room's type and name. A name it recognises
 * ("Kitchen") settles the room type and its design temperature from MIS
 * 3005-D Table 1; one it does not is left at the caller's fallback type AND
 * flagged for the designer to confirm — the same treatment a scan's
 * unrecognised label already gets. Nothing here decides a design temperature.
 */
export function drawnPlanCapture(plan: DrawnPlan): CapturedStructure {
  const rooms: CapturedRoom[] = [...plan.rooms]
    .sort((a, b) => a.story - b.story || a.id.localeCompare(b.id))
    .map((room) => {
      const floorY = drawnStoreyFloorY(plan, room.story);
      const axes = drawnRoomAxes(room);
      const sections: CapturedSection[] = [
        { identifier: `${room.id}:section`, label: room.name, story: room.story },
      ];
      return {
        identifier: room.id,
        story: room.story,
        walls: drawnRoomWalls(room).map((wall) => wallSurface(room, wall, floorY)),
        floors: [floorSurface(room, axes, floorY)],
        doors: [],
        windows: [],
        openings: [],
        objects: [],
        sections,
      };
    });
  return { identifier: "drawn", rooms };
}

/**
 * One wall: its run across the plan for column 0, straight up for column 1,
 * and the horizontal normal those two make for column 2 (which points into the
 * room, consistently for all four — the wall pairing that finds internal
 * partitions compares |dot| of two normals, so the sign does not matter, and
 * a consistent one is easier to reason about than an arbitrary one).
 */
function wallSurface(room: DrawnRoom, wall: DrawnWall, floorY: number): CapturedSurface {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const l = Math.hypot(dx, dy) || 1;
  const u = { x: dx / l, y: dy / l };
  const centre = { x: (wall.start.x + wall.end.x) / 2, y: (wall.start.y + wall.end.y) / 2 };
  return {
    identifier: drawnWallId(room.id, wall.index),
    dimensions: [round6(wall.lengthM), round6(room.ceilingHeightM), 0],
    transform: [
      u.x, 0, u.y, 0,
      0, 1, 0, 0,
      -u.y, 0, u.x, 0,
      round6(centre.x), round6(floorY + room.ceilingHeightM / 2), round6(centre.y), 1,
    ],
    story: room.story,
  };
}

/** The room's floor: flat, square to the room, its normal straight up. */
function floorSurface(
  room: DrawnRoom,
  axes: { readonly x: PlanPoint; readonly y: PlanPoint },
  floorY: number,
): CapturedSurface {
  // The floor's own height axis is the room's depth axis turned so that
  // column 0 x column 1 points up rather than down — see `redrawnFloor` in
  // plan-edits.ts, which builds a redrawn floor the same way.
  const b = { x: axes.x.y, y: -axes.x.x };
  return {
    identifier: drawnFloorId(room.id),
    dimensions: [round6(room.widthM), round6(room.depthM), 0],
    transform: [
      axes.x.x, 0, axes.x.y, 0,
      b.x, 0, b.y, 0,
      0, 1, 0, 0,
      round6(room.centre.x), round6(floorY), round6(room.centre.y), 1,
    ],
    story: room.story,
  };
}

